// Draws the demo scene — floor, taped H, a board outline and a mannequin
// rider — from the simulated camera's point of view.

import { REFERENCE, boardHalfWidth } from '../core/board';
import type { Body } from './body';
import { projector, v3, type SimWorld, type Vec3 } from './world';

type Proj = ReturnType<typeof projector>;

const COLORS = {
  wall: '#1a2229',
  floorNear: '#8a7355',
  floorFar: '#5c4b37',
  grid: 'rgba(0,0,0,0.10)',
  tape: '#3b82f6',
  board: 'rgba(255,255,255,0.13)',
  suit: '#1d2731',
  suitLight: '#2a3a48',
  skin: '#d9a77c',
  shadow: 'rgba(0,0,0,0.22)',
};

export function renderScene(ctx: CanvasRenderingContext2D, w: SimWorld, body: Body | null): void {
  const { width: W, height: H } = w.camera;
  const P = projector(w.camera);
  const px = (p: Vec3) => {
    const q = P(p);
    return { x: q.x * W, y: q.y * H, depth: q.depth, scale: q.scale };
  };

  // Wall and floor.
  ctx.fillStyle = COLORS.wall;
  ctx.fillRect(0, 0, W, H);
  const floor = [v3(-600, -500, 0), v3(600, -500, 0), v3(600, 700, 0), v3(-600, 700, 0)];
  const fp = clipToFront(floor, w).map(px);
  if (fp.length > 2) {
    const minY = Math.min(...fp.map((p) => p.y));
    const g = ctx.createLinearGradient(0, minY, 0, H);
    g.addColorStop(0, COLORS.floorFar);
    g.addColorStop(1, COLORS.floorNear);
    ctx.fillStyle = g;
    poly(ctx, fp);
    ctx.fill();
  }
  // Floorboards for depth cues.
  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  for (let x = -600; x <= 600; x += 18) line3(ctx, P, W, H, w, v3(x, -500, 0), v3(x, 700, 0));

  // Board outline: a ~6′ shortboard the tape stands in for (tail → nose), with
  // thruster fins near the tail so the back-foot placement reads correctly.
  const D = w.tape.gap;
  const tail = w.board.tail;
  const nose = w.board.nose;
  const len = nose - tail;
  const outline: Vec3[] = [];
  for (let i = 0; i <= 48; i++) {
    const t = i / 48;
    outline.push(v3(boardHalfWidth(t), tail + len * t, 0));
  }
  const pts = [...outline, ...outline.slice().reverse().map((p) => v3(-p.x, p.y, 0))].map(px);
  ctx.fillStyle = COLORS.board;
  poly(ctx, pts);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  const side = REFERENCE.sideFinFromTail;
  for (const [fx, fy] of [[-15, tail + side], [15, tail + side], [0, tail + REFERENCE.centreFinFromTail]] as const) {
    const a = px(v3(fx, fy - 6, 0));
    const b = px(v3(fx, fy + 4, 0));
    ctx.beginPath();
    ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.max(2, 2.2 * a.scale), Math.max(3, Math.hypot(b.x - a.x, b.y - a.y) / 2), Math.atan2(b.y - a.y, b.x - a.x) + Math.PI / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  // Tape H: stringer and two strips, ~5 cm wide.
  const L = w.tape.stripLength / 2;
  const tape = (a: Vec3, b: Vec3) => {
    const pa = px(a);
    const pb = px(b);
    ctx.strokeStyle = COLORS.tape;
    ctx.lineCap = 'butt';
    ctx.lineWidth = Math.max(2, 4.8 * ((pa.scale + pb.scale) / 2));
    ctx.beginPath();
    ctx.moveTo(pa.x, pa.y);
    ctx.lineTo(pb.x, pb.y);
    ctx.stroke();
  };
  tape(v3(0, tail + 2, 0), v3(0, nose - 4, 0));
  tape(v3(-L, 0, 0), v3(L, 0, 0));
  tape(v3(-L, D, 0), v3(L, D, 0));
  // Nose marker.
  tape(v3(-7, nose - 16, 0), v3(0, nose - 4, 0));
  tape(v3(7, nose - 16, 0), v3(0, nose - 4, 0));

  if (body) drawBody(ctx, px, body);
}

function drawBody(ctx: CanvasRenderingContext2D, px: (p: Vec3) => { x: number; y: number; depth: number; scale: number }, b: Body) {
  // Soft shadow under the body.
  const centre = [b.lHip, b.rHip, b.lShoulder, b.rShoulder].reduce((a, p) => v3(a.x + p.x / 4, a.y + p.y / 4, 0), v3(0, 0, 0));
  const sc = px(centre);
  ctx.fillStyle = COLORS.shadow;
  ctx.beginPath();
  ctx.ellipse(sc.x, sc.y, 45 * sc.scale, 16 * sc.scale, 0, 0, Math.PI * 2);
  ctx.fill();

  type Seg = { a: Vec3; b: Vec3; w: number; color: string };
  const segs: Seg[] = [
    { a: b.lHip, b: b.lKnee, w: 15, color: COLORS.suit },
    { a: b.rHip, b: b.rKnee, w: 15, color: COLORS.suit },
    { a: b.lKnee, b: b.lFoot.ankle, w: 11, color: COLORS.suit },
    { a: b.rKnee, b: b.rFoot.ankle, w: 11, color: COLORS.suit },
    { a: b.lFoot.heel, b: b.lFoot.toe, w: 8, color: COLORS.skin },
    { a: b.rFoot.heel, b: b.rFoot.toe, w: 8, color: COLORS.skin },
    { a: b.lShoulder, b: b.lElbow, w: 9, color: COLORS.suitLight },
    { a: b.rShoulder, b: b.rElbow, w: 9, color: COLORS.suitLight },
    { a: b.lElbow, b: b.lWrist, w: 7, color: COLORS.skin },
    { a: b.rElbow, b: b.rWrist, w: 7, color: COLORS.skin },
  ];
  const mid = (s: Seg) => px(v3((s.a.x + s.b.x) / 2, (s.a.y + s.b.y) / 2, (s.a.z + s.b.z) / 2)).depth;
  const torsoDepth = px(v3((b.lHip.x + b.rShoulder.x) / 2, (b.lHip.y + b.rShoulder.y) / 2, (b.lHip.z + b.rShoulder.z) / 2)).depth;
  const items: { depth: number; draw: () => void }[] = segs.map((s) => ({
    depth: mid(s),
    draw: () => {
      const p = px(s.a);
      const q = px(s.b);
      ctx.strokeStyle = s.color;
      ctx.lineCap = 'round';
      ctx.lineWidth = Math.max(2, s.w * ((p.scale + q.scale) / 2));
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(q.x, q.y);
      ctx.stroke();
    },
  }));
  items.push({
    depth: torsoDepth,
    draw: () => {
      const q = [b.lShoulder, b.rShoulder, b.rHip, b.lHip].map(px);
      ctx.fillStyle = COLORS.suitLight;
      ctx.strokeStyle = COLORS.suitLight;
      ctx.lineJoin = 'round';
      ctx.lineWidth = 12 * q[0].scale;
      poly(ctx, q);
      ctx.fill();
      ctx.stroke();
    },
  });
  const head = px(b.head);
  // Hair sits behind and above the face; whichever is nearer the camera is drawn last.
  const hairPt = v3(b.head.x - b.fwd.x * 3.5 + b.up.x * 3, b.head.y - b.fwd.y * 3.5 + b.up.y * 3, b.head.z - b.fwd.z * 3.5 + b.up.z * 3);
  const hair = px(hairPt);
  const drawHair = () => {
    ctx.fillStyle = '#3a2a1d';
    ctx.beginPath();
    ctx.arc(hair.x, hair.y, 10 * hair.scale, 0, Math.PI * 2);
    ctx.fill();
  };
  const drawFace = () => {
    ctx.fillStyle = COLORS.skin;
    ctx.beginPath();
    ctx.arc(head.x, head.y, 10.5 * head.scale, 0, Math.PI * 2);
    ctx.fill();
  };
  items.push({
    depth: head.depth,
    draw: () => {
      if (hair.depth > head.depth) {
        drawHair();
        drawFace();
      } else {
        drawFace();
        drawHair();
      }
    },
  });
  items.sort((a, b2) => b2.depth - a.depth).forEach((i) => i.draw());
}

function poly(ctx: CanvasRenderingContext2D, pts: { x: number; y: number }[]) {
  ctx.beginPath();
  pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  ctx.closePath();
}

/** Keeps the part of a floor polygon in front of the camera (avoids wrap-around). */
function clipToFront(pts: Vec3[], w: SimWorld): Vec3[] {
  const c = w.camera;
  const f = { x: c.target.x - c.pos.x, y: c.target.y - c.pos.y, z: c.target.z - c.pos.z };
  const n = Math.hypot(f.x, f.y, f.z);
  const depth = (p: Vec3) => ((p.x - c.pos.x) * f.x + (p.y - c.pos.y) * f.y + (p.z - c.pos.z) * f.z) / n - 20;
  const out: Vec3[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const da = depth(a);
    const db = depth(b);
    if (da >= 0) out.push(a);
    if (da >= 0 !== db >= 0) {
      const t = da / (da - db);
      out.push(v3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t));
    }
  }
  return out;
}

function line3(ctx: CanvasRenderingContext2D, P: Proj, W: number, H: number, w: SimWorld, a: Vec3, b: Vec3) {
  const seg = clipToFront([a, b], w);
  if (seg.length < 2) return;
  const p = P(seg[0]);
  const q = P(seg[1]);
  ctx.beginPath();
  ctx.moveTo(p.x * W, p.y * H);
  ctx.lineTo(q.x * W, q.y * H);
  ctx.stroke();
}
